// ws-adapter.js
export default class WSClient {
  constructor(
    url,
    {
      protocols = [],
      // ping/keepalive
      autoPing = false,          // startuj ping odmah po open, ili posle ACK-a ručno pozovi enablePing()
      pingInterval = 25000,
      pingEvent = "ping",
      pingData = {},
      // reconnect
      maxBackoff = 10000,        // max delay izmedju reconnect pokušaja
      // ser/deser (po difoltu ["event", data])
      serialize = (event, data) => JSON.stringify([event, data]),
      parse = defaultParse,
    } = {}
  ) {
    this.url = url;
    this.protocols = protocols;

    this.autoPing = autoPing;
    this.pingInterval = pingInterval;
    this.pingEvent = pingEvent;
    this.pingData = pingData;

    this.maxBackoff = maxBackoff;

    this.serialize = serialize;
    this.parse = parse;

    this.ws = null;
    this.handlers = new Map();     // event -> Set<fn>
    this.anyHandlers = new Set();  // Set<fn>
    this._reconnectAttempts = 0;
    this._pingTimer = null;
    this._manuallyClosed = false;

    this._connect();
  }

  // ---------- lifecycle ----------
  _connect() {
    this.ws = new WebSocket(this.url, this.protocols);

    this.ws.addEventListener("open", () => {
      this._reconnectAttempts = 0;
      if (this.autoPing) this._startPing();
      this._emitLocal("connect");
    });

    this.ws.addEventListener("message", (ev) => {
      let parsed;
      try {
        parsed = this.parse(ev.data);
      } catch {
        return; // tiho ignoriši nevalidan JSON
      }
      const { event, data } = parsed;
      this._emitLocal(event, data);
      for (const fn of this.anyHandlers) fn(event, data);
    });

    this.ws.addEventListener("close", () => {
      this._stopPing();
      this._emitLocal("disconnect");
      if (!this._manuallyClosed) this._scheduleReconnect();
    });

    this.ws.addEventListener("error", (err) => {
      this._emitLocal("connect_error", err);
      try { this.ws.close(); } catch {}
    });
  }

  _scheduleReconnect() {
    const base = 500 * Math.pow(1.6, this._reconnectAttempts++);
    const jitter = Math.random() * 200;
    const delay = Math.min(base + jitter, this.maxBackoff);
    setTimeout(() => this._connect(), delay);
  }

  // ---------- ping ----------
  _startPing() {
    if (this._pingTimer) return;
    this._pingTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === 1) {
        try {
          this.ws.send(this.serialize(this.pingEvent, this.pingData));
        } catch {}
      }
    }, this.pingInterval);
  }
  _stopPing() {
    if (this._pingTimer) {
      clearInterval(this._pingTimer);
      this._pingTimer = null;
    }
  }
  enablePing() { this._startPing(); }
  disablePing() { this._stopPing(); }

  // ---------- api ----------
  on(event, fn) {
    if (event === "*") { this.anyHandlers.add(fn); return this; }
    if (!this.handlers.has(event)) this.handlers.set(event, new Set());
    this.handlers.get(event).add(fn);
    return this;
  }
  off(event, fn) {
    if (event === "*") { this.anyHandlers.delete(fn); return this; }
    if (this.handlers.has(event)) this.handlers.get(event).delete(fn);
    return this;
  }
  onAny(fn)  { this.anyHandlers.add(fn); return this; }
  offAny(fn) { this.anyHandlers.delete(fn); return this; }

  emit(event, data) {
    if (!this.ws || this.ws.readyState !== 1) return;
    try { this.ws.send(this.serialize(event, data)); } catch {}
  }

  // raw slanje već pripremljenog frame-a (npr. ["login_visual", {...}])
  sendFrame(frame) {
    if (!this.ws || this.ws.readyState !== 1) return;
    try { this.ws.send(JSON.stringify(frame)); } catch {}
  }

  disconnect() {
    this._manuallyClosed = true;
    this._stopPing();
    if (this.ws) { try { this.ws.close(); } catch {} }
  }

  get readyState() { return this.ws ? this.ws.readyState : 3; } // 0-CONNECTING,1-OPEN,2-CLOSING,3-CLOSED
  get isOpen() { return this.readyState === 1; }

  // ---------- internal ----------
  _emitLocal(event, data) {
    const set = this.handlers.get(event);
    if (set) for (const fn of set) fn(data);
  }
}

// ---------- default parser ----------
// Pokriva:
// 1) ["event", data]
// 2) {"eventName": data}  (single-key objekat koje tvoj BE šalje u odgovorima)
// 3) {event, data} ili {type, ...}
function defaultParse(raw) {
  const m = JSON.parse(raw);

  // 1) Array-frame: ["event", data]
  if (Array.isArray(m)) return { event: m[0], data: m[1] };

  // 2) Single-key object: {"some_event": ...}
  if (m && typeof m === "object" && !("event" in m) && !("type" in m)) {
    const keys = Object.keys(m);
    if (keys.length === 1) return { event: keys[0], data: m[keys[0]] };
  }

  // 3) Object-frame: {event, data} ili {type, ...}
  return { event: m.event || m.type || "message", data: m.data ?? m };
}
